import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Button from '@mui/material/Button';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectUser } from 'renderer/features/auth/state/userSelectors';
import AddCardDialog from './AddCardDialog';
import DeleteCardDialog from './DeleteCardDialog';
import PaymentMethodCard from './PaymentMethodCard';
import { useListPaymentMethodsQuery } from '../services/paymentMethods.endpoints';
import { PaymentMethod } from '../schemas/paymentMethods.schemas';

const BillingTab: React.FC = () => {
  const { data: cards, isLoading, error } = useListPaymentMethodsQuery('');
  const user = useAppSelector(selectUser);
  const userId = user.role === 'public' ? '' : user._id;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentMethodFormOpen, setPaymentMethodFormOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [selectedCard, setSelectedCard] = useState<PaymentMethod | undefined>();

  const handleDeleteClick = (card: PaymentMethod) => {
    setSelectedCard(card);
    setDeleteDialogOpen(true);
  };

  // Separate Stripe (Subscription) and Braintree (Other Payment Methods) cards
  const stripeCards = cards?.filter((card: any) => card.stripeId);
  const braintreeCards = cards?.filter((card: any) => card.braintreeId);

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 'bold', marginBottom: 5, textAlign: 'center' }}
      >
        Payment Methods
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center', // Center the columns horizontally
          width: '100%',
        }}
      >
        <Grid container spacing={4} maxWidth={1200}>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ mr: 1 }}>
                Subscriptions
              </Typography>

              <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  setPaymentMethodFormOpen(true);
                  setMode('create');
                  setSelectedCard(undefined);
                }}
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                Add Card
              </Button>
            </Box>

            {isLoading && (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress />
              </Box>
            )}
            {error && (
              <Alert severity="error" sx={{ textAlign: 'center' }}>
                <Typography>{formatQueryError(error)}</Typography>
              </Alert>
            )}

            {/* Cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {stripeCards &&
                stripeCards.map((card) => (
                  <PaymentMethodCard
                    key={card._id}
                    card={card}
                    onEditIconClick={() => {
                      setSelectedCard(card);
                      setMode('edit');
                      setPaymentMethodFormOpen(true);
                    }}
                    onDeleteIconClick={() => handleDeleteClick(card)}
                  />
                ))}
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                pl: { md: 4 }, // Padding on medium screens and above
              }}
            >
              <Typography variant="h6" align="center">
                Other Payment Methods (Plugins)
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  mt: 2,
                }}
              >
                {braintreeCards &&
                  braintreeCards.map((card) => (
                    <PaymentMethodCard
                      key={card._id}
                      card={card}
                      onEditIconClick={() => {
                        setSelectedCard(card);
                        setMode('edit');
                        setPaymentMethodFormOpen(true);
                      }}
                      onDeleteIconClick={() => handleDeleteClick(card)}
                    />
                  ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <AddCardDialog
        open={paymentMethodFormOpen}
        onClose={() => setPaymentMethodFormOpen(false)}
        mode={mode}
        userId={userId}
        card={selectedCard}
      />

      <DeleteCardDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        card={selectedCard}
      />
    </Box>
  );
};

export default BillingTab;
